import React, { useEffect, useState } from "react";
import { Landmark, CreditCard, Loader2, Check, Settings2, Unlink, ArrowRight, Clock } from "lucide-react";
import api from "@/lib/api";

/**
 * PayoutManager — the recipient-side Cash Gift Payout flow. Connects a REAL bank account or
 * debit card via Stripe Connect Express (the same live pipe already used elsewhere in
 * GIFTED), then lets the recipient Cash Out their Maison Wallet balance to it. Recipients
 * are NEVER forced to spend on the platform — this component is always rendered with equal
 * visual weight to any on-platform spend option, wherever it's embedded (Claim screen,
 * Maison Wallet, Account Settings).
 *
 * Props:
 *  - balance: current Maison Wallet balance available to cash out (number)
 *  - compact: smaller footprint for embedding inside a denser panel (e.g. Account Settings)
 *  - onCashedOut: callback(amount) fired after a successful cash-out, so the parent can
 *    refresh its own balance/ledger view.
 */
const PayoutManager = ({ balance = 0, compact = false, onCashedOut }) => {
  const [status, setStatus] = useState(null); // {connected, onboarded, payouts_enabled, method_hint}
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(""); // "bank" | "card" | ""
  const [cashingOut, setCashingOut] = useState(false);
  const [cashoutAmount, setCashoutAmount] = useState(balance);
  const [result, setResult] = useState(null); // {amount, eta}
  const [error, setError] = useState("");
  const [managing, setManaging] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await api.get("/wallet/payout/status");
      setStatus(res.data);
    } catch (e) {
      console.error("Payout status load failed:", e);
    } finally {
      setLoadingStatus(false);
    }
  };
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setCashoutAmount(balance); }, [balance]);

  // Returning from Stripe's hosted onboarding (?payout_onboard=done) — refresh status once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payout_onboard") === "done") {
      loadStatus();
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  }, []);

  const connect = async (method) => {
    setConnecting(method); setError("");
    try {
      const res = await api.post("/wallet/payout/connect", { method });
      window.location.href = res.data.onboarding_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start payout setup.");
      setConnecting("");
    }
  };

  const cashOut = async () => {
    setCashingOut(true); setError(""); setResult(null);
    try {
      const res = await api.post("/wallet/payout/cashout", { amount: Number(cashoutAmount) || balance });
      setResult({ amount: res.data.amount, eta: res.data.eta });
      onCashedOut?.(res.data.amount);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not cash out right now.");
    } finally {
      setCashingOut(false);
    }
  };

  const openDashboard = async () => {
    setManaging(true); setError("");
    try {
      const res = await api.get("/wallet/payout/dashboard-link");
      window.open(res.data.url, "_blank");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open your payout dashboard yet — finish connecting first.");
    } finally {
      setManaging(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your payout method? You can reconnect a bank account or debit card any time.")) return;
    setDisconnecting(true); setError("");
    try {
      await api.post("/wallet/payout/disconnect");
      await loadStatus();
      setResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loadingStatus) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 className="w-4 h-4 animate-spin" /> Checking payout setup…</div>;
  }

  const methodLabel = status?.method_hint === "card" ? "Debit Card" : "Bank Account";

  return (
    <div data-testid="payout-manager" className={compact ? "" : "surface rounded-[1.75rem] p-6"}>
      {!compact && (
        <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><Landmark className="w-5 h-5 text-primary" /> Cash Out</h3>
      )}
      <p className={`text-sm text-muted-foreground ${compact ? "mb-3" : "mb-4"}`}>
        Your money, your choice — send it to your own bank account or debit card any time. You&apos;re never required to spend it in the Maison.
      </p>

      {error && <div data-testid="payout-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">{error}</div>}

      {result ? (
        <div data-testid="payout-success" className="surface rounded-2xl p-4 text-center">
          <Check className="w-8 h-8 text-secondary mx-auto mb-2" />
          <p className="font-serif text-2xl mb-1">${Number(result.amount).toFixed(2)} on its way</p>
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Estimated arrival: {result.eta}</p>
          <button onClick={() => setResult(null)} className="text-xs text-primary hover:underline mt-3">Done</button>
        </div>
      ) : !status?.connected ? (
        /* Two equally prominent, simple, trustworthy choices — both lead to Stripe's own
           secure hosted onboarding, where bank account OR debit card can be added. */
        <div className="grid sm:grid-cols-2 gap-3" data-testid="payout-connect-options">
          <button type="button" data-testid="payout-connect-bank" onClick={() => connect("bank")} disabled={!!connecting}
            className="flex flex-col items-center gap-2 surface rounded-2xl p-5 hover:border-primary/40 disabled:opacity-60 text-center">
            <Landmark className="w-7 h-7 text-primary" />
            <span className="font-medium text-sm">Bank Account</span>
            <span className="text-xs text-muted-foreground">Direct transfer</span>
            {connecting === "bank" && <Loader2 className="w-4 h-4 animate-spin text-primary mt-1" />}
          </button>
          <button type="button" data-testid="payout-connect-card" onClick={() => connect("card")} disabled={!!connecting}
            className="flex flex-col items-center gap-2 surface rounded-2xl p-5 hover:border-primary/40 disabled:opacity-60 text-center">
            <CreditCard className="w-7 h-7 text-primary" />
            <span className="font-medium text-sm">Digital Wallet</span>
            <span className="text-xs text-muted-foreground">Debit card payout</span>
            {connecting === "card" && <Loader2 className="w-4 h-4 animate-spin text-primary mt-1" />}
          </button>
        </div>
      ) : !status?.payouts_enabled ? (
        <div data-testid="payout-pending-onboarding" className="surface rounded-2xl p-4 text-center">
          <p className="text-sm text-muted-foreground mb-3">You started connecting a {methodLabel.toLowerCase()} — finish the last step with Stripe to enable Cash Out.</p>
          <button onClick={() => connect(status?.method_hint || "bank")} disabled={!!connecting}
            className="inline-flex items-center gap-1.5 text-sm bg-gradient-to-r from-primary to-secondary text-white rounded-full px-4 py-2 hover:opacity-90 disabled:opacity-60">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Finish setup <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </div>
      ) : (
        <div data-testid="payout-ready" className="space-y-3">
          <div className="flex items-center justify-between surface rounded-2xl px-4 py-3">
            <span className="text-sm flex items-center gap-2">
              {status.method_hint === "card" ? <CreditCard className="w-4 h-4 text-secondary" /> : <Landmark className="w-4 h-4 text-secondary" />}
              <span className="text-secondary font-medium">{methodLabel} connected</span>
            </span>
            <div className="flex items-center gap-3">
              <button type="button" data-testid="payout-manage-btn" onClick={openDashboard} disabled={managing}
                className="text-xs text-primary hover:underline flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" /> Manage</button>
              <button type="button" data-testid="payout-disconnect-btn" onClick={disconnect} disabled={disconnecting}
                className="text-xs text-muted-foreground hover:text-red-400 flex items-center gap-1"><Unlink className="w-3.5 h-3.5" /> Disconnect</button>
            </div>
          </div>

          {balance > 0 ? (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input type="number" min="0" max={balance} step="0.01" data-testid="payout-cashout-amount"
                  value={cashoutAmount} onChange={(e) => setCashoutAmount(e.target.value)}
                  className="w-full bg-input border border-border rounded-full pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
              </div>
              <button type="button" data-testid="payout-cashout-btn" onClick={cashOut}
                disabled={cashingOut || !cashoutAmount || Number(cashoutAmount) <= 0 || Number(cashoutAmount) > balance}
                className="flex items-center gap-1.5 text-sm bg-gradient-to-r from-secondary to-primary text-white rounded-full px-5 py-2.5 hover:opacity-90 disabled:opacity-60 whitespace-nowrap">
                {cashingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Cash Out</>}
              </button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">Your balance is $0.00 — claim a cash gift to cash it out here any time.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PayoutManager;
